<template>
  <el-dialog
    :model-value="visible"
    :width="isMobile ? '90%' : '500px'"
    @update:model-value="emit('update:visible', $event)"
    :show-close="true"
    center
    class="mobile-dialog"
  >
    <template #header>
      <div class="dialog-header">
        <div class="customer-service-icon" @mouseenter="showQRCode = true" @mouseleave="showQRCode = false">
          <el-icon class="service-icon"><Service /></el-icon>
          <div class="qr-code-popup" v-if="showQRCode">
            <img src="../assets/kefu.png" alt="客服二维码" class="qr-code-image" />
            <div class="qr-code-text">扫码联系客服</div>
          </div>
        </div>
        <span class="dialog-title">订单下载</span>
      </div>
    </template>
    <div class="order-query-container">
      <div class="input-section">
        <el-input
          v-model="orderNumber"
          placeholder="30001700678150404"
          class="order-input"
          size="large"
        />
        <el-button 
          @click="searchOrder" 
          class="search-btn"
          type="primary"
          size="large"
          :icon="Search"
        >
        </el-button>
      </div>
      
      <div class="tip-text">
        订单号可在微信或支付宝消费账单的<span class="highlight">商户订单号</span>中获取；
      </div>
      
      <div class="result-section" v-if="hasSearched">
        <div class="result-title">查询结果</div>
        <div class="result-content">
          <div v-if="!queryResult" class="no-result">
            查询不到该订单！
          </div>
          <div v-else class="order-info">
            <!-- 这里可以显示订单信息 -->
            <div class="order-detail">
              <div class="order-item">
                <span class="label">订单号：</span>
                <span class="value">{{ queryResult.orderNumber }}</span>
              </div>
              <div class="order-item">
                <span class="label">状态：</span>
                <span class="value" v-if="queryResult.status !== '异常'">{{ queryResult.status }}</span>
                <span class="value error-status" v-else>生成异常，请联系客服</span>
              </div>
              <div class="download-section" v-if="queryResult.status === '已生成' && queryResult.ossPath">
                <el-button type="primary" @click="downloadOrder" class="mobile-button">下载订单</el-button>
              </div>
            </div>
          </div>
        </div>
        
        <div class="refresh-section">
          <el-button @click="refreshQuery" class="refresh-btn mobile-button">
            刷新
          </el-button>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
import { defineProps, ref, defineEmits, watch, computed } from 'vue';
import { Search, Service } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import { get } from '../api/api';

interface OrderResult {
  orderNumber: string;
  title: string;
  status: string;
  downloadUrl?: string;
  ossPath?: string;
}

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  presetOrderNumber: {
    type: String,
    default: '',
  },
});

// 检测是否为移动设备
const isMobile = computed(() => {
  return window.innerWidth <= 768;
});

const emit = defineEmits(['update:visible']);

const orderNumber = ref('');
const queryResult = ref<OrderResult | null>(null);
const hasSearched = ref(false);
const showQRCode = ref(false);

// 监听预设订单号的变化
watch(() => props.presetOrderNumber, (newOrderNumber) => {
  if (newOrderNumber) {
    orderNumber.value = newOrderNumber;
    // 只填入订单号，不自动执行查询
  }
}, { immediate: true });

const searchOrder = async () => {
  if (!orderNumber.value.trim()) {
    ElMessage({
      message: '请输入订单号',
      type: 'warning',
    });
    return;
  }

  hasSearched.value = true;

  try {
    // 调用实际的API来查询订单
    const response = await get('/aiGenerate/getOrderDocumentDownload', { orderNumber: orderNumber.value });
    const data = response?.data?.data;
    if (!data) {
      queryResult.value = null;
      ElMessage({
        message: '查询不到该订单',
        type: 'info',
      });
      return;
    }
    // 处理status字段
    let statusText = '';
    switch (data.status) {
      case 0:
        statusText = '生成中';
        break;
      case 1:
        statusText = '已生成';
        break;
      case 99:
        statusText = '异常';
        break;
      default:
        statusText = String(data.status);
    }
    queryResult.value = {
      orderNumber: data.orderNumber || orderNumber.value,
      title: data.title || '',
      status: statusText,
      downloadUrl: data.downloadUrl,
      ossPath: data.ossPath,
    };
    ElMessage({
      message: '查询完成',
      type: 'info',
    });
  } catch (error) {
    queryResult.value = null;
    ElMessage({
      message: '查询失败，请重试',
      type: 'error',
    });
  }
};

const refreshQuery = () => {
  if (orderNumber.value.trim()) {
    searchOrder();
  }
};

const downloadOrder = () => {
  if (queryResult.value?.ossPath) {
    // 创建一个临时的a标签来触发下载
    const link = document.createElement('a');
    link.href = queryResult.value.ossPath;
    link.download = `订单_${queryResult.value.orderNumber}.docx`;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    ElMessage({
      message: '开始下载',
      type: 'success',
    });
  } else {
    ElMessage({
      message: '下载链接不可用',
      type: 'error',
    });
  }
};
</script>

<style scoped>
.order-query-container {
  padding: 20px 0;
}

.input-section {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.order-input {
  flex: 1;
}

.search-btn {
  width: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tip-text {
  font-size: 14px;
  color: #666;
  margin-bottom: 30px;
  line-height: 1.5;
}

.highlight {
  color: #ff4444;
  font-weight: bold;
}

.result-section {
  border-top: 1px solid #eee;
  padding-top: 20px;
}

.result-title {
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 20px;
}

.result-content {
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
}

.no-result {
  font-size: 16px;
  color: #666;
}

.order-info {
  width: 100%;
}

.order-detail {
  background: #f9f9f9;
  padding: 15px;
  border-radius: 8px;
}

.order-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.order-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.label {
  font-weight: bold;
  color: #333;
}

.value {
  color: #666;
}

.error-status {
  color: #ff4444;
  font-weight: bold;
}

.download-section {
  margin-top: 15px;
  text-align: center;
}

.refresh-section {
  text-align: center;
}

.refresh-btn {
  background: #f0f0f0;
  border: 1px solid #ddd;
  color: #666;
  padding: 8px 20px;
  border-radius: 4px;
}

.refresh-btn:hover {
  background: #e0e0e0;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 24px;
}

.customer-service-icon {
  position: relative;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background-color 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
}

.customer-service-icon:hover {
  background-color: #f5f5f5;
}

.service-icon {
  font-size: 18px;
  color: #409eff;
}

.qr-code-popup {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  margin-top: 10px;
  text-align: center;
  min-width: 150px;
}

.qr-code-popup::before {
  content: '';
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid white;
}

.qr-code-popup::after {
  content: '';
  position: absolute;
  top: -7px;
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-bottom: 6px solid #ddd;
}

.qr-code-image {
  width: 120px;
  height: 120px;
  display: block;
  margin: 0 auto 8px;
  border-radius: 8px;
}

.qr-code-text {
  font-size: 12px;
  color: #666;
  margin-top: 5px;
}

.dialog-title {
  font-size: 16px;
  font-weight: bold;
  color: #333;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .mobile-dialog :deep(.el-dialog) {
    margin: 5vh auto !important;
    max-width: 90vw;
  }
  
  .mobile-dialog :deep(.el-dialog__body) {
    padding: 16px;
  }
  
  .input-section {
    flex-direction: column;
    gap: 12px;
  }
  
  .order-input {
    width: 100%;
  }
  
  .search-btn {
    width: 100%;
  }
  
  .tip-text {
    font-size: 12px;
    line-height: 1.4;
    margin: 16px 0;
  }
  
  .result-title {
    font-size: 14px;
    margin-bottom: 12px;
  }
  
  .order-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    margin-bottom: 12px;
  }
  
  .label {
    font-size: 13px;
    min-width: auto;
  }
  
  .value {
    font-size: 13px;
  }
  
  .download-section {
    margin-top: 16px;
  }
  
  .download-section .el-button {
    width: 100%;
  }
  
  .refresh-section {
    margin-top: 16px;
  }
  
  .refresh-btn {
    width: 100%;
  }
  
  .dialog-header {
    height: 20px;
    gap: 8px;
  }
  
  .service-icon {
    font-size: 16px;
  }
  
  .dialog-title {
    font-size: 14px;
  }
  
  .qr-code-popup {
    min-width: 120px;
    padding: 12px;
  }
  
  .qr-code-image {
    width: 100px;
    height: 100px;
  }
  
  .qr-code-text {
    font-size: 11px;
  }
  
  .mobile-button {
    width: 100%;
    height: 44px;
    font-size: 15px;
  }
}
</style> 